import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
interface LegalModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}
export function LegalModal({
  isOpen,
  onClose,
  title,
  children
}: LegalModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);
  return <AnimatePresence data-id="element-171">
      {isOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-172">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} data-id="element-173" />
          <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.97
      }} transition={{
        duration: 0.3,
        ease: [0.21, 0.47, 0.32, 0.98]
      }} className="relative w-full max-w-2xl max-h-[80vh] rounded-2xl bg-dark-card border border-dark-border shadow-2xl shadow-black/50 flex flex-col" role="dialog" aria-modal="true" aria-label={title} data-id="element-174">
            <div className="flex items-center justify-between p-6 border-b border-dark-border flex-shrink-0" data-id="element-175">
              <h2 className="text-xl font-display font-bold text-gray-100" data-id="element-176">
                {title}
              </h2>
              <button onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:text-gray-100 hover:bg-white/5 transition-colors focus:outline-none focus:ring-2 focus:ring-cyan" aria-label="Close" data-id="element-177">
                <XIcon className="h-5 w-5" data-id="element-178" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto text-gray-400 leading-relaxed space-y-4 text-sm" data-id="element-179">
              {children}
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}
export function PrivacyContent() {
  return <>
      <p className="text-gray-300 font-medium" data-id="element-180">Last updated: March 2026</p>

      <h3 className="text-lg font-display font-semibold text-gray-200 pt-2" data-id="element-181">
        1. Information We Collect
      </h3>
      <p data-id="element-182">
        When you purchase the Brain-Sync System, we collect only the information
        necessary to process your transaction: your email address and payment
        details. Payment processing is handled by secure third-party providers —
        we never store your credit card information.
      </p>

      <h3 className="text-lg font-display font-semibold text-gray-200 pt-2" data-id="element-183">
        2. How We Use Your Information
      </h3>
      <p data-id="element-184">
        Your email address is used solely to deliver your purchase and send
        important product updates. We will never sell, rent, or share your
        personal information with third parties for marketing purposes.
      </p>

      <h3 className="text-lg font-display font-semibold text-gray-200 pt-2" data-id="element-185">
        3. Cookies & Analytics
      </h3>
      <p data-id="element-186">
        Our website may use minimal analytics cookies to understand how visitors
        interact with the page. These cookies do not collect personally
        identifiable information. You can disable cookies in your browser
        settings at any time.
      </p>

      <h3 className="text-lg font-display font-semibold text-gray-200 pt-2" data-id="element-187">
        4. Data Security
      </h3>
      <p data-id="element-188">
        We implement industry-standard security measures to protect your
        personal information. However, no method of transmission over the
        internet is 100% secure, and we cannot guarantee absolute security.
      </p>

      <h3 className="text-lg font-display font-semibold text-gray-200 pt-2" data-id="element-189">
        5. Your Rights
      </h3>
      <p data-id="element-190">
        You have the right to access, correct, or delete your personal data at
        any time. To exercise these rights, please reach out through the
        channels provided with your purchase.
      </p>

      <h3 className="text-lg font-display font-semibold text-gray-200 pt-2" data-id="element-191">
        6. Changes to This Policy
      </h3>
      <p data-id="element-192">
        We may update this Privacy Policy from time to time. Any changes will be
        reflected on this page with an updated revision date.
      </p>
    </>;
}
export function TermsContent() {
  return <>
      <p className="text-gray-300 font-medium" data-id="element-193">Last updated: March 2026</p>

      <h3 className="text-lg font-display font-semibold text-gray-200 pt-2" data-id="element-194">
        1. Product Description
      </h3>
      <p data-id="element-195">
        The Brain-Sync System is a digital information product consisting of PDF
        guides and AI prompt templates. It is designed for educational and
        productivity purposes only and is not a substitute for professional
        medical advice, diagnosis, or treatment.
      </p>

      <h3 className="text-lg font-display font-semibold text-gray-200 pt-2" data-id="element-196">
        2. Digital Delivery
      </h3>
      <p data-id="element-197">
        Upon successful payment, you will receive immediate access to download
        the complete Brain-Sync System. As this is a digital product, all sales
        are final once the download link has been delivered.
      </p>

      <h3 className="text-lg font-display font-semibold text-gray-200 pt-2" data-id="element-198">
        3. Intellectual Property
      </h3>
      <p data-id="element-199">
        All content within the Brain-Sync System is protected by copyright. You
        are granted a personal, non-transferable license to use the materials.
        You may not redistribute, resell, or share the product without written
        permission.
      </p>

      <h3 className="text-lg font-display font-semibold text-gray-200 pt-2" data-id="element-200">
        4. Disclaimer
      </h3>
      <p data-id="element-201">
        The Brain-Sync System is not medical advice. It does not diagnose,
        treat, or cure ADHD or any other condition. Results vary by individual.
        Consult a qualified healthcare professional for medical concerns.
      </p>

      <h3 className="text-lg font-display font-semibold text-gray-200 pt-2" data-id="element-202">
        5. Limitation of Liability
      </h3>
      <p data-id="element-203">
        To the maximum extent permitted by law, we shall not be liable for any
        indirect, incidental, or consequential damages arising from the use of
        this product.
      </p>

      <h3 className="text-lg font-display font-semibold text-gray-200 pt-2" data-id="element-204">
        6. Governing Law
      </h3>
      <p data-id="element-205">
        These terms shall be governed by and construed in accordance with
        applicable laws. Any disputes shall be resolved through good-faith
        negotiation before pursuing formal legal action.
      </p>
    </>;
}